/**
 * §3.6 live-call monitor client.
 *
 * Polls `/api/admin/live-calls` for the list of currently-active voice
 * sessions and opens `/ws/admin/live` to stream token-level transcript
 * deltas, tool calls, and per-response cost snapshots.
 *
 * Tenant isolation + auth are enforced server-side (live_monitor.py);
 * this module just forwards the JWT access token + tenant_id header.
 */

import { api } from "./api";
import { getAccessToken } from "./auth";

export interface LiveCall {
  call_id: string;
  tenant_id: string;
  mode: string;
  caller_number: string;
  started_at: number;
  total_cost_aed: number;
  input_tokens: number;
  output_tokens: number;
  // §v3.live-resolve / §v3.live-intel (2026-05-22) — optional fields the
  // backend now stamps on the session dict; they ride the existing
  // active_sessions() poll, so no new WS frame type is needed.
  /** Channel-side address (Telegram chat_id / WhatsApp wa_id) for chat sessions. */
  chat_id?: string;
  chat_channel?: string;
  /** Rolling enquiry-intent label (order_placed | support_issue | info_only | …). */
  intent_label?: string;
  intent_confidence?: string;
  /** Rolling sentiment label (positive | neutral | negative). */
  sentiment_label?: string;
}

export async function listLiveCalls(): Promise<LiveCall[]> {
  const { calls } = await api<{ calls: LiveCall[] }>("/api/admin/live-calls");
  return calls;
}

/**
 * §v3.stale-reap — force-close a stuck / zombie live-call entry.
 *
 * The server-side TTL reaper drops sessions that haven't seen an event
 * for 60s (configurable). This endpoint is the manual kick: if an
 * operator sees a session card lingering in the UI after the caller
 * hung up, clicking "End call" hits this and the card disappears
 * immediately. The backend publishes a synthetic ``session_end`` frame
 * so the WS-connected dashboards also update without waiting for the
 * next 3s poll.
 */
export async function forceCloseLiveCall(callId: string): Promise<void> {
  await api<{ ok: boolean }>(`/api/admin/live-calls/${encodeURIComponent(callId)}`, {
    method: "DELETE",
  });
}

// §live-monitor-readonly (2026-06-23) — operator-override controls
// (takeover / end-takeover / whisper / send-chat) were removed: the agent
// replaces the human operator, so the Live Monitor is observe-only. The
// only remaining mutation is forceCloseLiveCall above (zombie-session reap).

export type LiveFrame =
  | {
      type: "session_start";
      call_id: string;
      tenant_id: string;
      mode: string;
      caller_number: string;
      started_at: number;
      ts: number;
    }
  | {
      type: "transcript_delta";
      call_id: string;
      tenant_id: string;
      role: "assistant" | "user";
      delta: string;
      turn_id: string;
      ts: number;
      /** §v3.transcript-delta-replace (2026-05-14) — when ``true``,
       *  the reducer should REPLACE the streaming turn's text with
       *  ``delta`` verbatim. Used by the side-channel xAI STT which
       *  emits cumulative partial text ("open" → "open upper" →
       *  "open upper band"). Default (false / absent) keeps the
       *  old append semantics used by the agent-side
       *  ``response.text.delta`` tap. Without honoring this flag
       *  the dashboard rendered concatenated garbage like
       *  "openopen upperopen upper band". */
      replace?: boolean;
    }
  | {
      type: "transcript_final";
      call_id: string;
      tenant_id: string;
      role: "assistant" | "user";
      text: string;
      timestamp: string;
      ts: number;
    }
  | {
      // §v3.live-monitor (Path C Phase 2) — chat-side frame.
      // ``call_id`` is the unified-history conversation_id (chat
      // sessions don't have a true call). The reducer in
      // ``LiveCalls.tsx::handleFrame`` treats this exactly like a
      // ``transcript_final`` for voice — appends a finalised turn
      // to the selected session's transcript pane.
      type: "chat_message";
      call_id: string;
      tenant_id: string;
      role: "assistant" | "user";
      text: string;
      // §v3.chat-image — optional `/api/media/{id}` URL for a
      // customer-sent photo; when present the bubble renders the image
      // inline. Omitted/empty for text-only turns.
      image_url?: string;
      // §chat-lifecycle — turn replayed from earlier that day when a
      // returning customer reopened the session; `happened_at` carries
      // the original ISO timestamp for wall-clock rendering.
      historical?: boolean;
      happened_at?: string;
      // §outbound-images — agent-sent product photos (absolute URLs).
      image_urls?: string[];
      timestamp: string;
      ts: number;
    }
  | {
      type: "tool_call";
      call_id: string;
      tenant_id: string;
      name: string;
      arguments: Record<string, unknown>;
      call_tool_id: string;
      ts: number;
    }
  | {
      type: "response_end";
      call_id: string;
      tenant_id: string;
      model: string;
      input_tokens: number;
      output_tokens: number;
      cost_aed: number;
      duration_seconds: number;
      ts: number;
    }
  | {
      // §v3.live-monitor (Path C Phase 3) — rolling risk classifier.
      // Backend (live_risk.py) emits this when the tier OR reason
      // changes for a session. The dashboard's session list reducer
      // keeps the latest per call_id and recolours the row.
      // ``tier`` is one of "calm" | "caution" | "intervention".
      type: "risk_update";
      call_id: string;
      tenant_id: string;
      tier: "calm" | "caution" | "intervention";
      reason: string;
      ts: number;
    }
  | {
      // §v3.live-monitor (Path C Phase 4) — AI confidence pip.
      // Backend (live_confidence.py) emits this when the AI's
      // response latency or tool-retry pattern crosses a threshold.
      // The dashboard's session row + transcript pane recolour the
      // confidence pip.
      // ``state`` is one of "ok" | "low" | "stuck".
      type: "confidence_update";
      call_id: string;
      tenant_id: string;
      state: "ok" | "low" | "stuck";
      reason: string;
      ts: number;
    }
  | {
      // §monitor-english (2026-08-12) — English mirror of a non-English turn.
      //
      // ADDITIVE and LATE BY DESIGN. The turn itself already arrived as a
      // ``transcript_final``; this lands a beat later, once the translation
      // comes back, and the reducer attaches it to the matching bubble. The
      // original is never replaced — an operator must always be able to read
      // what the customer actually said.
      //
      // Keyed by ``source_text``, not by a turn id: the transcript reducer
      // merges, replaces and de-duplicates bubbles (cumulative STT re-emits,
      // orphan sweeps), so any positional id would go stale. The original text
      // is stable and is exactly what the operator is looking at.
      //
      // ``status: "unavailable"`` means we KNOW the turn wasn't English but
      // couldn't translate it — the UI says so rather than showing the
      // original as though it were English.
      type: "transcript_translation";
      call_id: string;
      tenant_id: string;
      role: "assistant" | "user";
      source_text: string;
      /** Detected language code as the model reported it ("ar", "tl"). */
      language: string;
      /** Display name for the chip ("Arabic"). "" when unknown. */
      language_label: string;
      english: string;
      status: "ok" | "unavailable";
      ts: number;
    }
  | {
      type: "session_end";
      call_id: string;
      tenant_id: string;
      ended_at: number;
      duration_seconds: number;
      total_cost_aed: number;
      ts: number;
    }
  | { type: "subscribed"; call_ids: string[] }
  | { type: "pong" }
  | { type: "error"; message: string };

/**
 * §v3.live-monitor-fix4 (2026-05-13) — late-joiner transcript
 * backfill. Reads persisted transcript_segments from
 * unified_history via the new backend endpoint so an operator who
 * opens the page mid-call sees full history, not just the last 40
 * WS frames.
 */
export interface TranscriptBackfillSegment {
  seq: number;
  role: string;
  text: string;
  timestamp: string;
  message_id: string;
  source: string;
  // §monitor-english — the stored English mirror, so an operator who opens
  // the page mid-call sees the same translated backlog as one who was
  // watching from the start (missed WS frames are not replayed).
  /** English mirror; "" for English turns and for failed translations. */
  text_en?: string;
  /** "ok" | "unavailable" | "" (English turn / not attempted). */
  translation_status?: string;
  language_code?: string;
}

export interface TranscriptBackfillResponse {
  call_id: string;
  segments: TranscriptBackfillSegment[];
  since_seq: number;
  limit: number;
}

export async function fetchLiveCallTranscriptBackfill(
  callId: string,
  opts: { sinceSeq?: number; limit?: number } = {},
): Promise<TranscriptBackfillResponse> {
  const params = new URLSearchParams();
  if (opts.sinceSeq != null) params.set("since_seq", String(opts.sinceSeq));
  if (opts.limit != null) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return api<TranscriptBackfillResponse>(
    `/api/admin/live-calls/${encodeURIComponent(callId)}/transcript` +
      (qs ? `?${qs}` : ""),
  );
}

// §v3.live-monitor-fix2 (2026-05-13) — partysocket-backed reconnect.
//
// The old LiveMonitorSocket created one ``new WebSocket(url)`` and
// just stopped on ``onclose``. Browsers drop idle WebSockets after
// 30-60s and any reverse proxy in the path can drop them sooner; the
// failure was silent and required a page refresh to recover.
//
// partysocket@1.1.19 (Cloudflare-maintained successor to
// reconnecting-websocket) wraps the native WebSocket with:
//   • exponential backoff + full jitter on reconnect
//   • automatic re-subscribe on every reconnect (we re-issue the
//     ``subscribe`` op inside the ``open`` handler so a fresh JWT
//     replaces a stale one without a page refresh)
//   • binary-clean (we don't use binary frames yet but it supports
//     them if we add audio fan-out later)
//
// §v3.live-monitor-fix2-hotfix (2026-05-14) — use the underlying
// ``ReconnectingWebSocket`` (re-exported as ``WebSocket`` from
// partysocket) instead of ``PartySocket``. The default
// ``PartySocket`` class is for PartyKit's room-based routing and
// throws synchronously if ``path`` starts with a slash — our
// custom endpoint ``/ws/admin/live`` does, which crashed the
// dashboard on every visit to Live Calls. ``ReconnectingWebSocket``
// takes a plain UrlProvider (string OR async function returning a
// string) and has none of the PartyKit-specific validation.
//
// The public LiveMonitorSocket API is unchanged so call sites
// (LiveCalls.tsx) need only the new ``onStateChange`` opt-in
// callback for the "● connected/reconnecting" indicator.
import { WebSocket as ReconnectingWebSocket } from "partysocket";

export type LiveSocketState =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "closed";

interface LiveMonitorOptions {
  callIds: string[]; // ["all"] for the fan-hose
  onFrame: (frame: LiveFrame) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  /** §v3.live-monitor-fix2 — fired on every connection-state
   *  transition. The page renders this as a small chip in the
   *  Live Monitor header so operators know whether the stream is
   *  live or recovering. */
  onStateChange?: (state: LiveSocketState) => void;
}

/**
 * Wrapper around the `/ws/admin/live` socket. partysocket handles
 * reconnect + heartbeat; we own the subscription protocol and the
 * state machine that drives the "● live / reconnecting" indicator.
 */
export class LiveMonitorSocket {
  private ws: ReconnectingWebSocket | null = null;
  private closedByClient = false;
  private currentState: LiveSocketState = "idle";

  constructor(private readonly opts: LiveMonitorOptions) {}

  private setState(next: LiveSocketState): void {
    if (next === this.currentState) return;
    this.currentState = next;
    try {
      this.opts.onStateChange?.(next);
    } catch {
      // never let UI callbacks tear down the socket
    }
  }

  open(): void {
    this.setState("connecting");

    // §v3.live-monitor-fix2-hotfix (2026-05-14) — build the URL ourselves and
    // hand it to ``ReconnectingWebSocket`` as a ``UrlProvider`` function
    // (invoked on every (re)connection attempt). We do NOT use the PartySocket
    // subclass because it validates ``path`` against PartyKit's room-based URL
    // convention and throws synchronously on a leading slash — which crashed
    // the dashboard on every Live Calls visit.
    //
    // §sec — the JWT rides the WebSocket SUBPROTOCOL (base64url), NOT ?secret=,
    // so it stays out of access logs / browser history / Referer. Browsers
    // can't set an Authorization header on a WebSocket; the access token is
    // long-lived (no mid-session rotation) so reading it once at open() is fine.
    // § no-default-tenant (qa-wave6) — the old ?tenant_id= query param was
    // dead weight: the server derives the tenant from the JWT claim (or the
    // X-Tenant-ID header for server-side clients) and never read the query
    // string. Dropped so the URL can't imply a client-chosen tenant.
    const token = getAccessToken();
    const urlProvider = (): string => {
      const proto =
        window.location.protocol === "https:" ? "wss" : "ws";
      return `${proto}://${window.location.host}/ws/admin/live`;
    };
    const protocols = token
      ? ["xpand-secret", btoa(token).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")]
      : [];

    this.ws = new ReconnectingWebSocket(urlProvider, protocols, {
      // §v3.live-monitor-fix2 backoff tuning:
      //   connectionTimeout 4s — fast-fail if backend is down
      //   minReconnectionDelay 500ms — quick recovery from
      //     transient drops
      //   maxReconnectionDelay 15s — bounded so a long outage
      //     doesn't strand the dashboard
      //   reconnectionDelayGrowFactor 1.5 — slightly gentler than
      //     the partysocket default of 1.3 for the "many operators
      //     reconnect at once" thundering-herd case
      //   maxRetries default = Infinity — operators leave the
      //     page open all day; we always want to recover.
      connectionTimeout: 4000,
      minReconnectionDelay: 500,
      maxReconnectionDelay: 15000,
      reconnectionDelayGrowFactor: 1.5,
    });

    this.ws.addEventListener("open", () => {
      this.setState("live");
      try {
        this.ws?.send(
          JSON.stringify({
            op: "subscribe",
            call_ids: this.opts.callIds,
          }),
        );
      } catch {
        // partysocket buffers sends when not OPEN; a throw here
        // would mean the socket really is gone — let the reconnect
        // path handle it.
      }
      this.opts.onOpen?.();
    });

    this.ws.addEventListener("message", (ev: MessageEvent) => {
      try {
        const frame = JSON.parse(ev.data) as LiveFrame;
        this.opts.onFrame(frame);
      } catch {
        // ignore malformed frames — the server doesn't send them.
      }
    });

    this.ws.addEventListener("error", (ev: Event) => {
      this.opts.onError?.(ev);
    });

    this.ws.addEventListener("close", (ev: CloseEvent) => {
      // If the close wasn't intentional, partysocket will reconnect
      // on its own and fire another "open" event. We flag the state
      // so the UI can show a reconnecting indicator in the meantime.
      if (!this.closedByClient) {
        this.setState("reconnecting");
        this.opts.onClose?.(ev);
      } else {
        this.setState("closed");
      }
    });
  }

  updateSubscription(callIds: string[]): void {
    // partysocket's ``readyState`` mirrors the underlying socket
    // (1 = OPEN). Pull from the type to avoid the WebSocket name
    // dependency (partysocket exposes the same enum values).
    if (this.ws && this.ws.readyState === 1) {
      try {
        this.ws.send(
          JSON.stringify({ op: "subscribe", call_ids: callIds }),
        );
      } catch {
        // Will resync on next reconnect via the open handler.
      }
    }
  }

  /** Current socket state — useful for one-shot snapshot reads
   *  outside the onStateChange callback (e.g. computing whether
   *  to fetch the transcript backfill). */
  get state(): LiveSocketState {
    return this.currentState;
  }

  close(): void {
    this.closedByClient = true;
    this.setState("closed");
    this.ws?.close();
    this.ws = null;
  }
}
